import React, { useState, useEffect } from 'react';
import {
  TextInput,
  Field,
  FieldBody,
  FieldLabel,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Typeahead,
  TypeaheadItem,
  TypeaheadItemSuggestion,
  SearchIcon,
} from '@emorg-prd/react-standard';
import LocationService from '../../../../api/LocationService';
import SourceSystem from '../../../../api/SourceSystem';
import SourceSystemIdType from '../../../../api/SourceSystemIdType';
import BusinessLine from '../../../../api/BusinessLine';
import RealEstateInterestType from '../../../../api/RealEstateInterestType';
import ITSiteCode from '../../../../api/ITSiteCode';
import EPSBusinessLine from '../../../../api/EPSBusinessLine';
import EPSBusinessValueChain from '../../../../api/EPSBusinessValueChain';

export function CreateFromExisting(props) {
  const {
    onChange,
    handlePageError,
    closeModal,
    isVisible,
    showLoadingSpinner,
  } = props;
  const [existingLocationNameSearch, setExistingLocationNameSearch] = useState('');
  const [newLocationName, setNewLocationName] = useState('');
  const [locationSearchDropdown, setLocationSearchDropdown] = useState([]);
  const [realEstateInterest, setRealEstateInterest] = useState('');
  const [realEstateInterestType, setRealEstateInterestType] = useState('');
  const [realEstateInterestList, setRealEstateInterestList] = useState([]);
  const [locationToCopy, setLocationToCopy] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (existingLocationNameSearch.length > 2) {
      setIsLoading(true);
      const timeOutId = setTimeout(() => {
        LocationService.getBySearchText(existingLocationNameSearch).then((res) => {
          setLocationSearchDropdown(res.data);
          setIsLoading(false);
        }).catch((error) => {
          handlePageError(error.message);
        });
      }, 1000);
      return () => clearTimeout(timeOutId);
    } else if (existingLocationNameSearch.length === 0) {
      setIsLoading(false);
      setLocationSearchDropdown([]);
    }
  }, [existingLocationNameSearch]);

  function toggleAddREICheckbox(index) {
    let reiListCopy = [...realEstateInterestList];
    reiListCopy[parseInt(index)].checked =
      !realEstateInterestList[parseInt(index)].checked;
    setRealEstateInterestList(reiListCopy);
  }

  const resetExistingLocationValues = () => {
    closeModal();
    setExistingLocationNameSearch('');
    setNewLocationName('');
    setRealEstateInterest('');
    setRealEstateInterestType('');
    setRealEstateInterestList([]);
    setIsLoading(false);
  };

  function handleLocationSelect(e, value) {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
    setRealEstateInterest('');
    setRealEstateInterestType('');
    setNewLocationName(value + ' Copy');
    setExistingLocationNameSearch(value);
    const locationMatch = locationSearchDropdown.find(
      (location) => location.locationName === value
    );
    setLocationToCopy(locationMatch);
    let tempREIList = [];
    let i = 0;
    locationMatch.realEstateInterest.forEach((rei) => {
      if (rei.isPrimaryRealEstateInterest) {
        setRealEstateInterest(rei.realEstateInterestName);
        setRealEstateInterestType(rei.realEstateInterestType.realEstateInterestTypeName);
      } else {
        tempREIList.push({
          id: i,
          typeId: rei.realEstateInterestTypeId,
          type: rei.realEstateInterestType.realEstateInterestTypeName,
          name: rei.realEstateInterestName,
          checked: false,
        });
      }
      i++;
    });
    setRealEstateInterestList(tempREIList);
  }

  const copyExistingValuesToForm = async (e) => {
    try {
      closeModal();
      onChange('locationName', newLocationName);
      onChange(
        'locationStreetAddress1',
        locationToCopy.locationStreetAddress1 ? locationToCopy.locationStreetAddress1 : ''
      );
      onChange(
        'locationStreetAddress2',
        locationToCopy.locationStreetAddress2 ? locationToCopy.locationStreetAddress2 : ''
      );
      onChange(
        'locationCity',
        locationToCopy.locationCity ? locationToCopy.locationCity : ''
      );
      onChange('country', {
        id: locationToCopy.site.country.countryId,
        name: locationToCopy.site.country.countryName,
      });
      onChange('siteCountryId', locationToCopy.site.country.countryId);
      if (locationToCopy.countrySubDivision) {
        onChange('countrySubdivision', {
          id: locationToCopy.countrySubDivision.countrySubdivisionId,
          name: locationToCopy.countrySubDivision.countrySubdivisionName,
        });
      }
      onChange(
        'locationPostalCode',
        locationToCopy.locationPostalCode ? locationToCopy.locationPostalCode : ''
      );
      onChange('latitude', locationToCopy.locationLat ? locationToCopy.locationLat : '');
      onChange(
        'longitude',
        locationToCopy.locationLong ? locationToCopy.locationLong : ''
      );
      if (locationToCopy.timeZone) {
        onChange('timezone', {
          timezoneId: locationToCopy.timeZone.timezoneId,
          timezoneName: locationToCopy.timeZone.timezoneName,
        });
      }
      onChange(
        'locationDesc',
        locationToCopy.locationDesc ? locationToCopy.locationDesc : ''
      );
      //REI populate
      if (locationToCopy.realEstateInterest) {
        let realEstateInterestObjectList = [];
        let realEstateInterestDropdown = [];
        let copiedREI = false;
        let realEstateInterestNonPrimary = locationToCopy.realEstateInterest.filter(x => x.isPrimaryRealEstateInterest !== 1);
        realEstateInterestNonPrimary.forEach(async (element, index) => {
          if (realEstateInterestList[parseInt(index)].checked) {
            copiedREI = true;
            showLoadingSpinner(true);
            let realEstateInterestTypeIdResponse = await RealEstateInterestType.get(
              element.realEstateInterestTypeId
            );
            showLoadingSpinner(false);
            let realEstateInterestObject = {
              isNew: false,
              realEstateInterestId: element.realEstateInterestId,
              realEstateInterestName: element.realEstateInterestName,
              realEstateInterestTypeId: element.realEstateInterestTypeId,
              realEstateInterestTypeName: realEstateInterestTypeIdResponse.realEstateInterestTypeName,
              realEstateInterestDesc: element.realEstateInterestDesc,
              primary: false,
              isREILocked: false,
              realEstateInterestLatitude: element.realEstateInterestLatitude,
              realEstateInterestLongitude: element.realEstateInterestLongitude,
              realEstateInterestFrontageSize: element.realEstateInterestFrontageSize,
              realEstateInterestDepthSize: element.realEstateInterestDepthSize,
              realEstateInterestArea: element.realEstateInterestArea,
              realEstateInterestSizeUOM: element.realEstateInterestSizeUOM,
              realEstateInterestBusinessLineAlias: element.realEstateInterestBusinessLineAlias,
              realEstateInterestSourceSystemRelate:
                element.realEstateInterestSourceSystemRelateData.map((source) => ({
                  realEstateInterestSourceSystemId: source.sourceSystemId,
                  realEstateInterestSourceSystemName: source.sourceSystem?.sourceSystemName,
                  realEstateInterestSourceSystemTypeId: source.sourceSystemIdTypeId,
                  realEstateInterestSourceSystemIdTypeName: source.sourceSystemIdType?.sourceSystemIdTypeName,
                  realEstateInterestSourceSystemReferenceID: source.sourceSystemReferenceId,
                })),
            };
            realEstateInterestDropdown.push([]);
            realEstateInterestObjectList.push(realEstateInterestObject);
            if (realEstateInterestObjectList.length === 1) {
              realEstateInterestObjectList[0].primary = true;
            }
          }
        });
        if (copiedREI) {
          onChange('realEstateInterestList', realEstateInterestObjectList);
          onChange('realEstateInterestDropdown', realEstateInterestDropdown);
        } else {
          let blankREI = [];
          let reiDropdown = [];
          blankREI.push({
            isNew: true,
            realEstateInterestName: newLocationName,
            realEstateInterestTypeId: '',
            realEstateInterestTypeName: '',
            primary: true,
            realEstateInterestSourceSystemRelate: [],
            realEstateInterestDesc: '',
            realEstateInterestLatitude: '',
            realEstateInterestLongitude: '',
            realEstateInterestFrontageSize: '',
            realEstateInterestDepthSize: '',
            realEstateInterestArea: '',
            realEstateInterestSizeUOM: '',
            realEstateInterestBusinessLineAlias: '',
          });
          reiDropdown.push([]);
          onChange('realEstateInterestList', blankREI);
          onChange('realEstateInterestDropdown', reiDropdown);
        }
      }
      if (locationToCopy.primaryUseType) {
        onChange('primaryUseType', {
          id: locationToCopy.primaryUseType.primaryUseTypeId,
          name: locationToCopy.primaryUseType.primaryUseTypeName,
        });
      }
      if (locationToCopy.operationStatus) {
        onChange('locationOperationStatus', {
          id: locationToCopy.operationStatus.operationStatusId,
          name: locationToCopy.operationStatus.operationStatusName,
        });
      }
      if (locationToCopy.fuelBrand) {
        onChange('fuelBrand', {
          id: locationToCopy.fuelBrand.fuelBrandId,
          name: locationToCopy.fuelBrand.fuelBrandName,
        });
      }
      if (locationToCopy.storeBrand) {
        onChange('storeBrand', {
          id: locationToCopy.storeBrand.storeBrandId,
          name: locationToCopy.storeBrand.storeBrandName,
        });
      }
      if (locationToCopy.affiliateOwnershipPercentage) {
        onChange(
          'affiliateOwnershipPercentage',
          locationToCopy.affiliateOwnershipPercentage
        );
      }
      if (locationToCopy.jvOwnershipPercentage) {
        onChange('jVOwnershipPercentage', locationToCopy.jvOwnershipPercentage);
        onChange('isJointVenture', true);
      }
      if (locationToCopy.locationStewardCostObject) {
        onChange(
          'stewardshipCostObjectList',
          locationToCopy.locationStewardCostObject.map((sco) => ({
            stewardshipTypeId: sco.stewardshipTypeId,
            stewardshipTypeName: sco.stewardshipType?.stewardshipTypeName,
            costObjectTypeId: sco.costObjectTypeId,
            costObjectTypeName: sco.costObjectType?.costObjectTypeName,
            costObject: sco.costObject,
          }))
        );
      }
      if (locationToCopy.locationBusinessLineRelate.length) {
        let businessLineList = [];
        locationToCopy.locationBusinessLineRelate.forEach(async (element) => {
          showLoadingSpinner(true);
          let filteredList = await BusinessLine.getByReportingUnit(
            element.businessLineReportingUnit.company_code
          );
          showLoadingSpinner(false);
          let bsLineFilteredList = [];
          filteredList.forEach((filterElement) => {
            bsLineFilteredList.push({
              business_division_code: filterElement.business_division_code,
              business_line_acronym: filterElement.business_line_acronym,
              business_line_code: filterElement.business_line_code,
              business_line_name: filterElement.business_line_name,
              businessLineReportUnit: element.businessLineReportingUnit.company_code
                ? element.businessLineReportingUnit.company_code
                : '',
            });
          });
          let businessLineObject = {
            allocationPercent: element.allocationPercent,
            businessLineCode: element.business_line_code,
            businessLineName: element.businessLine.business_line_name,
            businessLineReportUnitCompanyCode: element.businessLineReportingUnit.company_code,
            businessLineReportUnitName:
              element.businessLineReportingUnit.company_code + ' - ' +
              element.businessLineReportingUnit.company_name,
            businessLineFilteredList: bsLineFilteredList,
          };
          businessLineList.push(businessLineObject);
          onChange('businessLineAllocationList', businessLineList);
        });
      }
      if (locationToCopy.locationEPSBusinessLineRelate) {
        let epsBLObjectList = [];
        locationToCopy.locationEPSBusinessLineRelate.forEach(async (element) => {
          showLoadingSpinner(true);
          let elementEPSValueChain = await EPSBusinessValueChain.get(element.eps_business_vc_id);
          let elementEPSBusinessLine = await EPSBusinessLine.get(element.eps_business_line_id);
          showLoadingSpinner(false);
          let epsBLObject = {
            eps_business_vc_id: element.eps_business_vc_id,
            businessValueChain: elementEPSValueChain.short_description,
            eps_business_line_id: element.eps_business_line_id,
            businessLine: elementEPSBusinessLine.short_description,
            epsAllocationPercent: element.allocationPercent,
          };
          epsBLObjectList.push(epsBLObject);
        });
        onChange('epsBusinessLineAllocationList', epsBLObjectList);
      }
      onChange('siteName', locationToCopy.site.siteName);
      onChange('siteCode', locationToCopy.site.siteCode);
      onChange(
        'sitePrimaryUseType',
        locationToCopy.site.primaryUseType.primaryUseTypeName
      );
      onChange('sitePrimaryUseTypeId', locationToCopy.site.primaryUseTypeId);
      onChange(
        'siteOperationStatus',
        locationToCopy.site.operationStatus.operationStatusName
      );
      onChange('siteOperationStatusId', locationToCopy.site.operationStatusId);
      if (locationToCopy.site.itSiteCodeRelate) {
        let elementSiteCodeList = [];
        locationToCopy.site.itSiteCodeRelate.forEach(async (element) => {
          showLoadingSpinner(true);
          let itSiteCodeResponse = await ITSiteCode.get(element.itSiteCodeId);
          showLoadingSpinner(false);
          let elementValue = {
            itSiteCodeId: element.itSiteCodeId,
            itSiteCode: itSiteCodeResponse.itSiteCode,
            itSiteCodeDesc: itSiteCodeResponse.itSiteCodeDesc,
          };
          elementSiteCodeList.push(elementValue);
        });
        onChange('itSiteCodeList', elementSiteCodeList);
      }
      if (locationToCopy.site.siteSourceSystemRelate) {
        let siteSourceSystemRelateList = [];
        locationToCopy.site.siteSourceSystemRelate.forEach(async (element) => {
          showLoadingSpinner(true);
          let sourceSystemResponse = await SourceSystem.get(element.sourceSystem.sourceSystemId);
          let sourceSystemIdTypeResponse = await SourceSystemIdType.get(
            element.sourceSystemIdTypeId
          );

          let siteSourceSystemObject = {
            sourceSystemId: element.sourceSystemId,
            sourceSystemName: sourceSystemResponse.sourceSystemName,
            sourceSystemIdTypeName: sourceSystemIdTypeResponse.sourceSystemIdTypeName,
            sourceSystemIdTypeId: element.sourceSystemIdTypeId,
            sourceSystemReferenceId: element.sourceSystemReferenceId,
          };
          siteSourceSystemRelateList.push(siteSourceSystemObject);
        });
        onChange('siteSourceLinkList', siteSourceSystemRelateList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <Modal show={isVisible} onHide={resetExistingLocationValues} className='existing-location'>
        <div className='scrollPadding'>
          <ModalHeader>
            <h3>Create From Existing Location</h3>
          </ModalHeader>
          <ModalBody className='scrollBar' style={{ paddingRight: '30px' }}>
            <Field style={{ marginBottom: '30px' }}>
              <FieldLabel>Existing Location Name</FieldLabel>
              <FieldBody>
                <form className='em-c-search-form'>
                  <div className='em-c-search__body'>
                    <Typeahead
                      className='existing-search'
                      onChange={(e, value) => handleLocationSelect(e, value)}
                      inputProps={{
                        className: 'search-bar',
                        onChange: (e) => setExistingLocationNameSearch(e.target.value),
                        onKeyPress: (e, value) =>
                          (e.key === 'Enter' ? handleLocationSelect(e, value) : null),
                        value: existingLocationNameSearch,
                        placeholder: 'Search',
                      }}
                      fieldProps={{ note: 'Minimum of 3 letters to search' }}
                    >
                      {locationSearchDropdown
                        .filter((location) =>
                          location.locationName.toLowerCase()
                            .slice(0, location.locationName.trim().toLowerCase().length)
                          === location.locationName.trim().toLowerCase()
                        )
                        .map((location) => (
                          <TypeaheadItem
                            id={location.locationId}
                            key={location.locationId}
                            value={location.locationName}
                          >
                            {/* passing in locationId in both the Suggestion and the inner span bc e.target doesn't return consistently*/}
                            <TypeaheadItemSuggestion id={location.locationId}>
                              <span
                                id={location.locationId}
                              >{`${location.locationName} `}</span>
                            </TypeaheadItemSuggestion>
                          </TypeaheadItem>
                        ))}
                    </Typeahead>
                    <Button style={{ display: isLoading ? 'none' : 'block' }}>
                      <SearchIcon size='small' />
                    </Button>
                    <Button
                      style={{ display: isLoading ? 'block' : 'none', padding: '0 4.25px 0 4.25px' }}
                      loading
                      size='medium'
                    >
                      Loading
                    </Button>
                  </div>
                </form>
              </FieldBody>
            </Field>
            <TextInput
              className='text-input-new'
              label='New Location Name'
              placeholder=''
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
            />
            <div className='input-row'>
              <TextInput
                className='realEstateInterest'
                label='Real Estate Interest'
                placeholder=''
                value={realEstateInterest}
                onChange={(e) => setRealEstateInterest(e.target.value)}
                readOnly
              />
              <TextInput
                className='realEstateInterestType'
                label='Interest Type'
                placeholder=''
                value={realEstateInterestType}
                onChange={(e) => setRealEstateInterestType(e.target.value)}
                readOnly
              />
              <p style={{ paddingTop: '35px' }}> Existing Primary </p>
            </div>
            <Field>
              <FieldLabel></FieldLabel>
              <h6
                style={{
                  marginTop: '15px',
                  padding: '25px 0 15px 0',
                  display: realEstateInterestList.length > 0 ? 'block' : 'none',
                  borderTop: '1px solid var(--paletteSurface03,#e5e5e5)',
                }}
              >
                Add Real Estate Interest to New Location
              </h6>
              {realEstateInterestList.map((interest, index) => (
                <Field>
                  <FieldLabel></FieldLabel>
                  <div className='existing-rei-row'>
                    <TextInput
                      className='realEstateInterest'
                      label=''
                      placeholder={interest.name}
                      value={interest.name}
                      onChange={(e) => setRealEstateInterest(e.target.value)}
                      readOnly
                    />
                    <TextInput
                      className='realEstateInterestType'
                      label=''
                      style={{ padding: '' }}
                      placeholder=''
                      value={interest.type}
                      onChange={(e) => setRealEstateInterestType(e.target.value)}
                      readOnly
                    />
                    <p style={{ marginLeft: '5px', marginTop: '10px' }}>
                      <input
                        type='checkbox'
                        label='Add'
                        style={{ height: 'auto' }}
                        id={interest.id}
                        checked={interest.checked}
                        onChange={(e) => toggleAddREICheckbox(index)}
                      ></input>
                      Add
                    </p>
                  </div>
                </Field>
              ))}
              <Button
                style={{ marginRight: '10px', marginTop: '30px' }}
                className='small-margin'
                variant='secondary'
                onClick={resetExistingLocationValues}
              >
                Cancel
              </Button>
              <Button
                className='small-margin'
                variant='primary'
                onClick={(e) => copyExistingValuesToForm(e)}
              >
                Copy Location
              </Button>
            </Field>
          </ModalBody>
        </div>
      </Modal>
    </div>
  );
}
export default CreateFromExisting;
